import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, Label } from "@/components/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        source: "dossier-002-waitlist",
      });
      setStatus("done");
      toast.success("You're on the list. You'll be the first to know.");
      setEmail("");
      setTimeout(() => setStatus("idle"), 3500);
    } catch (err) {
      setStatus("idle");
      const code = err?.response?.status;
      toast.error(
        code === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <section
      data-testid="coming-soon"
      className="relative border-t border-white/8 bg-[#050505] overflow-hidden"
    >
      <div className="crimson-glow absolute top-1/2 right-[8%] h-[360px] w-[360px] -translate-y-1/2 rounded-full opacity-40" />

      <div className="relative mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeUp>
              <div className="inline-flex items-center gap-2.5 border border-white/12 px-4 py-2">
                <Lock size={14} className="text-crimson" />
                <Label className="!text-white/70">Coming Soon</Label>
              </div>
            </FadeUp>

            <FadeUp delay={0.05}>
              <p className="mt-8 font-mono-accent text-xs uppercase tracking-[0.35em] text-crimson">
                Dossier #002
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
                The Dark Psychology <br className="hidden md:block" />
                <span className="italic">Handbook</span>
              </h2>
            </FadeUp>

            <FadeUp delay={0.15}>
              <p className="mt-6 max-w-md text-white/50 text-base md:text-lg leading-relaxed">
                Be the first to know when it's released.
              </p>
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.2}>
              <form onSubmit={submit} data-testid="coming-soon-form" className="w-full">
                <div className="flex items-center gap-4 border-b border-white/20 pb-2 focus-within:border-[var(--noir-crimson)] transition-colors">
                  <input
                    data-testid="coming-soon-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    aria-label="Email address for Dossier #002 waitlist"
                    className="flex-1 bg-transparent py-3 text-lg text-white placeholder:text-white/30 outline-none font-display tracking-wide"
                  />
                </div>
                <motion.button
                  data-testid="coming-soon-submit"
                  type="submit"
                  disabled={status !== "idle"}
                  whileTap={{ scale: 0.98 }}
                  aria-label="Notify me when Dossier #002 is released"
                  className="group mt-6 flex w-full items-center justify-center gap-3 px-8 py-4 text-white font-mono-accent text-[11px] uppercase tracking-[0.25em] disabled:opacity-70 transition-transform"
                  style={{ background: "var(--noir-crimson)" }}
                >
                  {status === "loading" && <Loader2 size={16} className="animate-spin" />}
                  {status === "done" && <Check size={16} />}
                  <span>{status === "done" ? "You're on the List" : "Notify Me"}</span>
                  {status === "idle" && (
                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  )}
                </motion.button>
                <p className="mt-4 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/30">
                  Early access · No spam
                </p>
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
